import React, { FormEvent, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { Button, Text } from '@razorpay/blade/components';
import api from 'api';
import { BulkUploadDetailsSchemaContract } from 'schemas/BulkUploadDetailsSchema';
import { AppError } from 'utils/AppError';
import { BulkUploadStatus } from 'constants/bulkUpload';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { GET_BULK_UPLOAD_RECORD } from 'components/BulkUploads/queries/queries';
import { FileUploadStep } from './FileUploadStep';
import { InstructionsView } from './InstructionsView';
import { DownloadSampleTemplate } from './DownloadSampleTemplate';
import { useFormWithProvider } from 'forms';
import { useBulkUploadType } from '../hooks/useBulkUploadType';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useBulkUploadTypeConfig } from '../hooks/useBulkUploadTypeConfig';

export const BulkUploadView = () => {
  const bulkUploadType = useBulkUploadType();
  const uploadTypeConfig = useBulkUploadTypeConfig(bulkUploadType);
  const viewSubtitle = uploadTypeConfig.initiation.step1Subtitle;
  const instructions = uploadTypeConfig.initiation.instructions;
  const organizationId = useReduxSelector(loggedInUserSelectors.organizationID) as number;
  const { updateStepStatus } = useWizardInfo();
  const pollingRequestId = `upload_${bulkUploadType}_polling_request_id`;
  const onSampleTemplateDownload = uploadTypeConfig.initiation.onSampleTemplateDownload;

  const { register: _, ...extraFieldsData } = useFormWithProvider<Record<string, any>>();
  const ExtraFields = uploadTypeConfig.initiation.ExtraFields;

  const history = useHistory();
  const [bulkUploadRecord, setBulkUploadRecord] = useState<
    BulkUploadDetailsSchemaContract[0] | null
  >(null);

  const nextStep = () => {
    updateStepStatus(0, 'success');
    history.push({
      pathname: routePaths.bulkUploads.preview,
      search: `?type=${bulkUploadType}`,
    });
  };

  const bulkUploadRecordResponse = useQuery(
    GET_BULK_UPLOAD_RECORD,
    () => {
      return api.bulkUploads.fetchBulkUploadRecordByType(bulkUploadType);
    },
    {
      onSuccess: (data) => data && setBulkUploadRecord(data.length > 0 ? data[0] : null),
      onError: (error: typeof AppError) => {},
    },
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const onSubmitInitiation = uploadTypeConfig.initiation.onSubmitInitiation;
    if (onSubmitInitiation) {
      onSubmitInitiation({
        extraFieldsData: extraFieldsData,
        onSuccessCb: nextStep,
        bulkUploadRecord: bulkUploadRecord,
      });
    } else {
      nextStep();
    }
  };

  const handleSampleTemplateDownload = () => {
    onSampleTemplateDownload(organizationId, bulkUploadType);
  };

  return (
    <FormWrapper onSubmit={onSubmit}>
      <StepContentWrapper noBgIllustration maxWidth="500px">
        <StepHeader
          subtitle={uploadTypeConfig.initiation.step1Title}
          title=""
          marginBottom="spacing.4"
        />
        {viewSubtitle ? (
          <Text color="surface.text.gray.subtle" marginBottom="spacing.6">
            {viewSubtitle}
          </Text>
        ) : null}

        <DownloadSampleTemplate onDownloadTemplate={handleSampleTemplateDownload} />
        <InstructionsView instructions={instructions} />
        <FileUploadStep
          onNext={nextStep}
          pollingRequestId={pollingRequestId}
          bulkUploadRecord={bulkUploadRecord}
          setBulkUploadRecord={setBulkUploadRecord}
          bulkUploadRecordResponse={bulkUploadRecordResponse}
        />
        {!!ExtraFields && <ExtraFields />}
      </StepContentWrapper>
      <StepActionsWrapper maxWidth="500px">
        <span />
        <Button
          type="submit"
          variant="primary"
          isLoading={extraFieldsData.isSubmitting}
          isDisabled={
            bulkUploadRecord
              ? bulkUploadRecord.status !== BulkUploadStatus.STATUS_VALIDATION_SUCCESS
              : true
          }>
          Upload & Preview
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};
